<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import InputText from 'primevue/inputtext';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import axios from "axios";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "content-edit",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      v$: useVuelidate(),
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      contentId: this.$route.params.id,
      title: "",
      description: "",
      can_skip: false,
      is_file: true,
      fileData: "",
      link: "",
      formSending: false,
      lesson: null,
      lessons: [],
      chapter: null,
      chapters: [],
      type: null,
      typeOptions: [
        { label: 'ملف', value: 'pdf' },
        { label: 'فيديو', value: 'video' },
        { label: 'صوت', value: 'audio' },
        // { label: 'إمتحان', value: 'exam' }
      ],
    };
  },
  components: { Breadcrumb, InputText, Dropdown, InputSwitch },
  validations() {
    return {
      title: { required },
      type: { required },
      is_file: { required },
      lesson: { required },
    };
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope ? `/courses/${this.courseId}/contents` : `/${this.gradeSlug}/content`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: "المحتوى", path: this.basePath }, { name: "تعديل المحتوى", path: `${this.basePath}/edit/${this.contentId}` }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: "المحتوى", path: `/${this.gradeSlug}/content` },
        { name: "تعديل المحتوى", path: `/${this.gradeSlug}/content/edit/${this.contentId}` },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() { return this.resolvedGrade?.id ?? null; },
  },
  methods: {
    onFileChange(event) {
      this.fileData = event.target.files[0];
    },

    // ✅ Fetch the existing content data
    fetchContent() {
      axios.get(`/content/${this.contentId}`).then((res) => {
        const data = res.data.data;
        this.title = data.title;
        this.description = data.description;
        this.can_skip = data.can_skip === 1;
        this.is_file = data.is_file === 1;
        this.link = data.link ?? "";
        this.type = this.typeOptions.find(t => t.value === data.type) || null;

        // fetch lesson hierarchy
        this.fetchChapters().then(() => {
          this.chapter = this.chapters.find(c => c.id === data.lesson?.parent?.id) || null;
          if (this.chapter) {
            this.fetchLessons().then(() => {
              this.lesson = this.lessons.find(l => l.id === data.lesson?.id) || null;
            });
          }
        });
      });
    },

    // ✅ Update instead of create
    sendForm() {
      this.v$.$validate();
      if (!this.v$.$error) {
        const formData = new FormData();
        formData.append('title', this.title);
        formData.append('description', this.description);
        formData.append('lesson_id', this.lesson.id);
        formData.append('type', this.type?.value);
        formData.append('is_file', this.is_file ? 1 : 0);

        if (this.is_file && this.fileData) {
          formData.append('file', this.fileData);
        }
        if (!this.is_file && this.link) {
          formData.append('link', this.link);
        }

        this.formSending = true;
        axios
            .post(`/contents/${this.contentId}?_method=PUT`, formData) // Laravel-friendly way
            .then(() => {
              this.formSending = false;
              Swal.fire({ position: "center", icon: "success", title: "تم التعديل بنجاح" });
            })
            .catch((error) => {
              this.formSending = false;
              Swal.fire({ position: "center", icon: "error", title: error.response.data.message });
            });
      }
    },

    async fetchChapters() {
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId }
          : { type: 2, academic_year_id: this.gradeId };
      try {
        const response = await axios.get('/lessons', { params });
        this.chapters = response.data.data;
      } catch {
        this.chapters = [];
      }
    },
    async fetchLessons() {
      const chapterId = this.chapter?.id;
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId, parent_id: chapterId }
          : { type: 3, academic_year_id: this.gradeId, parent_id: chapterId };
      try {
        const response = await axios.get('/lessons', { params });
        this.lessons = response.data.data;
      } catch {
        this.lessons = [];
      }
    },

    customLabelChapter(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
    customLabelLesson(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    this.fetchContent();
  },
};
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <form class="row" @submit.prevent="sendForm()">
      <div class="col-12">
        <div class="tab-radio-group mb-4">
          <label
              v-for="(option, index) in typeOptions"
              :key="index"
              :class="['tab-radio', { active: type?.value === option.value }]"
          >
            <input type="radio" v-model="type" :value="option" class="d-none" />
            {{ option.label }}
          </label>
        </div>
      </div>

      <!-- Chapter -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الأبواب</label>
          <Dropdown v-model="chapter" :options="chapters" showClear :optionLabel="customLabelChapter"
                    placeholder="اختر الباب" @update:model-value="fetchLessons" />
        </div>
      </div>

      <!-- Lesson -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label>الدروس</label>
          <Dropdown v-model="lesson" :options="lessons" showClear :optionLabel="customLabelLesson"
                    placeholder="اختر الدرس" />
          <span class="text-danger" v-if="v$.lesson.$error">برجاء اختيار الدرس</span>
        </div>
      </div>

      <!-- Title -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="title">اسم المحتوى</label>
          <InputText id="title" v-model="title" aria-describedby="title-help" />
          <span class="text-danger" v-if="v$.title.$error">برجاء ادخال اسم المحتوى</span>
        </div>
      </div>

      <!-- Description -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="description">وصف المحتوى</label>
          <InputText id="description" v-model="description" aria-describedby="description-help" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4"></div>

      <!-- is_file -->
      <div class="col-lg-4 col-md-6 col-12 mb-4">
        <div class="d-flex flex-column gap-2">
          <label for="isFile">هل هو ملف؟</label>
          <InputSwitch id="isFile" v-model="is_file" />
        </div>
      </div>

      <!-- File / Link -->
      <div class="col-lg-4 col-md-6 col-12 mb-4" v-if="is_file">
        <label>الملف</label>
        <input type="file" @change="onFileChange" class="form-control" />
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4" v-else>
        <div class="d-flex flex-column gap-2">
          <label for="link">الرابط</label>
          <InputText id="link" v-model="link" aria-describedby="link-help" />
        </div>
      </div>
      <div class="col-lg-4 col-md-6 col-12 mb-4"></div>
      <div class="col-lg-4 col-md-6 col-12">
        <button type="submit" class="btn primaryButton w-100" :disabled="formSending">
          <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
          {{ formSending ? 'جاري الارسال...' : 'إرسال' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.tab-radio-group {
  display: flex;
  gap: 8px;
}
.tab-radio {
  padding: 8px 20px;
  border: 1px solid #007ad9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  color: #007ad9;
  width: 25%;
  text-align: center;
}
.tab-radio.active {
  background: #007ad9;
  color: #fff;
  font-weight: bold;
}
</style>
